import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../ui/card";
import { Button } from "../ui/button";
// import { Progress } from '../ui/progress';
import { Badge } from "../ui/badge";
import { Icon } from "@iconify/react";
import StatCard from "../ui/stat-card";
import BalanceCard from "../ui/balance-card";
import TransactionTable from "../ui/transaction-table";
import LineChart from "../ui/line-chart";
import transactionsData from "./transactions.json";
import salesData from "./sales-data.json";
import { TransactionsData } from "../../types/transaction/transaction";
import TargetCard from "../ui/targetCard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import SocialSourceCard from "../ui/socialCard";
import WeeklySalesCard from "../ui/weeklySalesCard";
import SalesMapCard from "../ui/SalesMapCard";

const Analytics: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("This Month");

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    // Add your filter logic here
  };

  return (
    <div className="p-4">
      <div className="space-y-6 w-full mx-auto  p-4">
        {/* Page Title */}
        <div className="mb-6">
          <div className="flex flex-col">
            <h4 className="text-xl font-bold text-black">Analytics</h4>
            <nav className="flex space-x-2 text-sm text-gray-500">
              <a href="#" className="hover:text-gray-700">
                Dashboard
              </a>
              <span>/</span>
              <span className="text-gray-700">Analytics</span>
            </nav>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatCard
            title="No. of Properties"
            value="2,854"
            icon="solar:buildings-2-broken"
            trend={{ value: 7.34, isPositive: true }}
            chartData={[45, 52, 38, 45, 19, 23, 35, 42, 38, 45, 52, 38]}
          />

          <StatCard
            title="Regi. Agents"
            value="705"
            icon="solar:users-group-two-rounded-broken"
            trend={{ value: 76.89, isPositive: true }}
            chartData={[35, 41, 36, 26, 45, 48, 52, 53, 41, 36, 26, 45]}
          />

          <StatCard
            title="Customers"
            value="9,431"
            icon="solar:shield-user-broken"
            trend={{ value: 45.0, isPositive: false }}
            chartData={[25, 31, 26, 16, 35, 38, 42, 43, 31, 26, 16, 35]}
          />

          <StatCard
            title="Revenue"
            value="$78.3M"
            icon="solar:money-bag-broken"
            trend={{ value: 8.76, isPositive: true }}
            chartData={[55, 61, 56, 46, 65, 68, 72, 73, 61, 56, 46, 65]}
          />
        </div>

        {/* Sales Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row justify-between items-center pb-2">
                <CardTitle>Sales Analytics</CardTitle>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-[5px] flex items-center gap-2"
                    >
                      {selectedPeriod}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handlePeriodChange("Today")}
                    >
                      Today
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handlePeriodChange("This Week")}
                    >
                      This Week
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handlePeriodChange("This Month")}
                    >
                      This Month
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handlePeriodChange("This Year")}
                    >
                      This Year
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="text-right mb-4">
                  <p className="text-lg font-medium text-gray-900">
                    <Icon
                      icon="ri:wallet-3-fill"
                      className="inline-block mr-2 text-white"
                    />
                    <span className="text-white">Earnings:</span>{" "}
                    <span className="text-blue-600 font-bold">$85,934</span>
                  </p>
                </div>
                <div className="h-80">
                  <LineChart data={salesData.salesData} height={320} />
                </div>
              </CardContent>
              <CardFooter className="bg-background p-6">
                <div className="grid grid-cols-3 gap-6 w-full">
                  <div className="border-r border-gray-700">
                    <p className="text-surface-2 dark:text-surface-2 mb-2 text-center">
                      Income
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-lg font-medium text-primary dark:text-primary">
                        23,675.00
                      </span>
                      <Badge className="bg-green-500/10 text-green-400 text-xs rounded-[5px]">
                        <Icon
                          icon="ri:arrow-up-line"
                          className="w-3 h-3 mr-1"
                        />
                        0.08%
                      </Badge>
                    </div>
                  </div>
                  <div className="border-r border-gray-700">
                    <p className="text-surface-2 dark:text-surface-2 mb-2 text-center text-primary dark:text-primary">
                      Expenses
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-lg font-medium text-primary dark:text-primary">
                        11,562.00
                      </span>
                      <Badge className="bg-red-500/10 text-red-400 text-xs rounded-[5px]">
                        <Icon
                          icon="ri:arrow-down-line"
                          className="w-3 h-3 mr-1"
                        />
                        5.38%
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-surface-2 dark:text-surface-2 mb-2 text-center text-primary dark:text-primary">
                      Balance
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-lg font-medium text-primary dark:text-primary">
                        67,365.00
                      </span>
                      <Badge className="bg-green-500/10 text-green-400 text-xs rounded-[5px]">
                        <Icon
                          icon="ri:arrow-up-line"
                          className="w-3 h-3 mr-1"
                        />
                        2.89%
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </div>

          {/* Balance Card */}
          <div className="lg:col-span-1">
            <BalanceCard
              balance={117000.43}
              income={13321.12}
              expense={7566.11}
              onSend={() => {}}
              onReceive={() => {}}
            />

            {/* <Card className="mt-6">
              <CardContent className="p-0">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 border-r">
                    <h5 className="card-title mb-0 text-gray-900 font-medium">Property</h5>
                    <div className="w-12 h-12 bg-gray-100/50 rounded-full mx-auto my-3 flex items-center justify-center">
                      <Icon icon="solar:home-bold-duotone" className="text-2xl text-primary" />
                    </div>
                    <h4 className="text-gray-900 font-medium">15,780</h4>
                    <p className="text-gray-500">60% Target</p>
                    <div className="mt-3 h-2.5 bg-gray-200 rounded-full">
                      <div className="h-2.5 bg-primary rounded-full animate-pulse" style={{ width: '60%' }}></div>
                    </div>
                  </div>

                  <div className="text-center p-3">
                    <h5 className="card-title mb-0 text-gray-900 font-medium">Revenue</h5>
                    <div className="w-12 h-12 bg-gray-100/50 rounded-full mx-auto my-3 flex items-center justify-center">
                      <Icon icon="solar:money-bag-bold-duotone" className="text-2xl text-green-500" />
                    </div>
                    <h4 className="text-gray-900 font-medium">$78.3M</h4>
                    <p className="text-gray-500">80% Target</p>
                    <div className="mt-3 h-2.5 bg-gray-200 rounded-full">
                      <div className="h-2.5 bg-green-500 rounded-full animate-pulse" style={{ width: '80%' }}></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card> */}
            <div className="mt-6">
              <TargetCard />
            </div>
          </div>
        </div>

        <div className="mt-6 mb-6 flex gap-6">
          <SocialSourceCard />
          <SalesMapCard />
          <WeeklySalesCard />
        </div>

        {/* Latest Transactions */}
        <TransactionTable
          title="Latest Transaction"
          transactions={(transactionsData as TransactionsData).transactions.map(
            (transaction) => ({
              ...transaction,
              onView: () => {},
              onEdit: () => {},
              onDelete: () => {},
            }),
          )}
          onFilterChange={() => {}}
          onSelectAll={(selected) => {}}
          onSelectTransaction={(id, selected) => {}}
        />
      </div>
    </div>
  );
};

export default Analytics;
